import {
  rectifyEndpointWaypoints, routeOrthogonal, shouldPruneWaypoints,
  simplifyOrthogonal, type Pt,
} from "../cable-routing";
import { buildPreviewObstacles, previewRoute, tidySystemCables } from "../cable-tidy";
import type { Cable, SystemNode } from "../types";
import type { CableRouteRequest, CableRouter, CableRouterId, ConnectionRouteRequest, RoutingScene } from "./types";

/** Adapter for the shipped routing pipeline; also owns shared waypoint policy. */
export class LegacyCableRouter implements CableRouter {
  readonly id: CableRouterId = "legacy";
  private readonly scenes = new WeakMap<SystemNode[], RoutingScene>();

  prepare(nodes: SystemNode[]): RoutingScene {
    let scene = this.scenes.get(nodes);
    if (!scene) {
      scene = { ...buildPreviewObstacles(nodes), nodes };
      this.scenes.set(nodes, scene);
    }
    return scene;
  }

  route(request: CableRouteRequest): Pt[] {
    const { source, sourceSide, target, targetSide, cable } = request;
    const waypoints = cable?.waypoints ?? [];
    const stale = request.movingEndpoint && request.phase === "drag" &&
      shouldPruneWaypoints(source, target, waypoints);
    // Do not compute an unused obstacle route for a cable with stored bends.
    if (waypoints.length > 0 && !stale) {
      return simplifyOrthogonal([
        source,
        ...rectifyEndpointWaypoints(source, sourceSide, target, targetSide, waypoints, {
          skipSource: request.sourceIsJunction,
          skipTarget: request.targetIsJunction,
        }),
        target,
      ]);
    }
    return this.preview(request);
  }

  protected preview(request: CableRouteRequest): Pt[] {
    const { source, sourceSide, target, targetSide, scene, cable } = request;
    return cable && scene
      ? previewRoute(scene, cable, source, sourceSide, target, targetSide)
      : this.connection(request);
  }

  connection({ source, sourceSide, target, targetSide }: ConnectionRouteRequest): Pt[] {
    return routeOrthogonal(source, target, sourceSide, targetSide);
  }

  settle(nodes: SystemNode[], cables: Cable[], ids: string[]): Map<string, Pt[]> {
    return tidySystemCables(nodes, cables, ids);
  }
}
